import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useProfile } from '@shared/api/useProfile';
import { useWebSocketContext } from '@shared/context/WebSocketProvider';
import { useChatStore } from '../model/chat.store';
import { wsParser, wsBuilder } from './chat.ws';
import { ChatMessageToast } from '@shared/ui/chat-message-toast/chat-message-toast';
import { REPLY_TYPE, EVENT_TYPE, USER_STATUS, MESSAGE_TYPE, PENDING_MESSAGE_TYPE, SENT_TO_CARD_TYPE } from './ws.types';

const HELLO_TIMEOUT = 5000;
const HEARTBEAT_INTERVAL = 3000;
const HEARTBEAT_TIMEOUT = 10000;

export const useChatSocket = () => {
  const { lastMessage, lastJsonMessage, sendJsonMessage, readyState, getWebSocket } = useWebSocketContext();
  const { data: profile } = useProfile();
  const navigate = useNavigate();

  const chatStoreRef = useChatStore;

  const pongTimeoutRef = useRef(null);
  const helloTimeoutRef = useRef(null);
  const lastProcessedRef = useRef(null);

  // Синхронизируем статус WebSocket
  useEffect(() => {
    const isOpen = readyState === 1;
    const isReconnecting = readyState === 0;
    chatStoreRef.getState().setWsConnected(isOpen);
    chatStoreRef.getState().setWsReconnecting(isReconnecting);
  }, [readyState, chatStoreRef]);

  // Ожидание Hello: если за 5 секунд после подключения не пришёл — реконнект
  useEffect(() => {
    if (readyState !== 1) return;

    chatStoreRef.getState().setLastPongAt(Date.now());
    helloTimeoutRef.current = setTimeout(() => {
      const state = chatStoreRef.getState();
      if (state.chatsLoading && state.chats.length === 0) {
        console.warn('WS Hello timeout: no HELLO received, forcing reconnect');
        // Принудительно закрываем соединение — react-use-websocket переподключится
        const ws = getWebSocket?.();
        if (ws) {
          ws.close();
        }
      }
    }, HELLO_TIMEOUT);

    return () => {
      if (helloTimeoutRef.current) clearTimeout(helloTimeoutRef.current);
    };
  }, [readyState, chatStoreRef, getWebSocket]);

  // Heartbeat
  useEffect(() => {
    if (!sendJsonMessage || readyState !== 1) return;

    const sendPing = () => {
      sendJsonMessage(wsBuilder.ping());

      if (pongTimeoutRef.current) clearTimeout(pongTimeoutRef.current);
      pongTimeoutRef.current = setTimeout(() => {
        const lastPong = chatStoreRef.getState().lastPongAt;
        if (!lastPong || Date.now() - lastPong > HEARTBEAT_TIMEOUT) {
          console.warn('WS heartbeat timeout: no PONG received');
        }
      }, HEARTBEAT_TIMEOUT);
    };

    sendPing();
    const interval = setInterval(sendPing, HEARTBEAT_INTERVAL);

    return () => {
      clearInterval(interval);
      if (pongTimeoutRef.current) clearTimeout(pongTimeoutRef.current);
    };
  }, [sendJsonMessage, readyState, chatStoreRef]);

  // Перезапрашиваем историю при смене таймзоны: бэк персонализует
  // card.lines (время в карточках) под таймзону зрителя, поэтому после
  // смены часового пояса нужно получить карточки заново
  const prevTimezoneRef = useRef(null);
  useEffect(() => {
    const timezone = profile?.timezone;
    const prevTimezone = prevTimezoneRef.current;
    prevTimezoneRef.current = timezone;
    if (!timezone || !prevTimezone || timezone === prevTimezone) return;

    const state = chatStoreRef.getState();
    const chatId = state.selectedChat?.id;
    if (!chatId || !state.wsConnected || state.messageHistory.length === 0) return;

    sendJsonMessage(wsBuilder.historyRequest(chatId, 0));
  }, [profile?.timezone, sendJsonMessage, chatStoreRef]);

  useEffect(() => {
    const incomingMessage = lastJsonMessage || lastMessage?.data;
    const processedMessage = lastMessage || lastJsonMessage;
    if (!incomingMessage) return;
    if (lastProcessedRef.current === processedMessage) return;
    lastProcessedRef.current = processedMessage;

    // Сначала пробуем распарсить как Reply
    const reply = wsParser.parseReply(incomingMessage);
    if (reply) {
      if (reply.error) {
        console.error('WS Reply error:', reply.error);
        toast.error(reply.error.message || 'Ошибка сервера чата');
        // Помечаем связанное оптимистичное сообщение failed
        const tempId = chatStoreRef.getState().takeCommandTempId(reply.id);
        if (tempId) {
          chatStoreRef.getState().failOptimisticMessage(tempId);
        }
        // Если ошибка по HISTORY — сбрасываем загрузку
        if (reply.type === REPLY_TYPE.HISTORY) {
          chatStoreRef.getState().setHistoryLoading(false);
          chatStoreRef.getState().setHistoryError(reply.error.message || 'Ошибка загрузки истории');
        }
        return;
      }

      // Команда выполнена с предупреждениями или отклонена.
      // Отклонение (success не true) — помечаем сообщение failed.
      // Успех с предупреждениями (например, сервер скрыл личные данные) —
      // сообщение принято, ждём EVENT MESSAGE с итоговым текстом.
      if (reply.warnings.length > 0 || reply.success === false) {
        const commandRejected = reply.success !== true;
        if (commandRejected) {
          const tempId = chatStoreRef.getState().takeCommandTempId(reply.id);
          if (tempId) {
            chatStoreRef.getState().failOptimisticMessage(tempId);
          }
        }
        const reason = reply.warnings.length > 0
          ? reply.warnings.join('; ')
          : 'Команда не выполнена';
        toast.warning(reason);
        return;
      }

      switch (reply.type) {
        case REPLY_TYPE.PONG:
          chatStoreRef.getState().setLastPongAt(Date.now());
          if (pongTimeoutRef.current) {
            clearTimeout(pongTimeoutRef.current);
            pongTimeoutRef.current = null;
          }
          break;

        case REPLY_TYPE.HISTORY: {
          const history = reply.history;
          if (history) {
            const currentChatId = chatStoreRef.getState().selectedChat?.id;
            if (currentChatId && history.chat_id !== currentChatId) {
              return;
            }
            const page = history.page ?? 0;
            const messages = history.messages || [];
            chatStoreRef.getState().setHistoryError(null);
            if (page === 0) {
              chatStoreRef.getState().setHistory(messages);
            } else {
              chatStoreRef.getState().appendHistory(messages);
            }
          }
          break;
        }

        default:
          break;
      }
      return;
    }

    // Затем пробуем распарсить как Event
    const event = wsParser.parseEvent(incomingMessage);
    if (event) {
      switch (event.type) {
        case EVENT_TYPE.HELLO: {
          chatStoreRef.getState().setChats(event.hello?.chats || []);
          // Отменяем таймаут ожидания Hello
          if (helloTimeoutRef.current) {
            clearTimeout(helloTimeoutRef.current);
            helloTimeoutRef.current = null;
          }
          break;
        }

        case EVENT_TYPE.MESSAGE: {
          const msg = event.message;
          if (!msg) break;

          const state = chatStoreRef.getState();
          const isCurrentChat = msg.chat_id === state.selectedChat?.id;

          // Проверяем, есть ли наше оптимистичное сообщение для подтверждения.
          // Интерактивные сообщения сервер возвращает с карточным типом и своим
          // payload ({entity_type, entity_id}), поэтому матчим по "виду"
          // отправленного предложения, а не по точному совпадению payload.
          const isIncomingInteractive =
            !!msg.type && msg.type !== MESSAGE_TYPE.TEXT;
          let optimisticMsg = null;

          if (isIncomingInteractive) {
            const pendingMine = state.messageHistory.filter(
              (m) =>
                m.pending &&
                m.sender_id === profile?.id &&
                m.chat_id === msg.chat_id &&
                !!m.type &&
                m.type !== MESSAGE_TYPE.TEXT
            );

            if (msg.type === MESSAGE_TYPE.SLOT_OFFER) {
              const p = msg.payload || {};
              optimisticMsg = pendingMine.find((m) => {
                const mp = m.payload || {};
                return (
                  m.type === PENDING_MESSAGE_TYPE.SLOT_OFFER &&
                  mp.date === p.date &&
                  mp.time === p.time &&
                  String(mp.instrument_id ?? '') === String(p.instrument_id ?? '')
                );
              });
            } else {
              // Карточки-сущности: package_offer -> package_offer_card и т.п.
              optimisticMsg = pendingMine.find(
                (m) => SENT_TO_CARD_TYPE[m.type] === msg.type
              );
            }
          } else {
            optimisticMsg = state.messageHistory.find(
              (m) =>
                m.pending &&
                m.sender_id === profile?.id &&
                m.chat_id === msg.chat_id &&
                (m.data || '') === (msg.data || '') &&
                (m.attachments?.[0]?.name || null) === (msg.attachments?.[0]?.name || null)
            );
          }

          if (optimisticMsg) {
            state.confirmOptimisticMessage(optimisticMsg.id, msg);
          } else if (isCurrentChat) {
            // Сервер мог изменить текст сообщения (например, скрыть личные
            // данные — "***"), поэтому матчинг по содержимому не сработал.
            // Подтверждаем самое старое своё pending-текстовое сообщение в чате.
            const pendingTextId =
              msg.sender_id === profile?.id &&
              (!msg.type || msg.type === MESSAGE_TYPE.TEXT)
                ? chatStoreRef
                    .getState()
                    .takeOldestPendingTextId(msg.chat_id, profile.id)
                : undefined;
            if (pendingTextId) {
              chatStoreRef.getState().confirmOptimisticMessage(pendingTextId, msg);
            } else {
              state.prependMessage(msg);
            }
          } else {
            state.prependMessageToChat(msg);
          }

          if (
            msg.sender_id !== profile?.id &&
            !window.location.pathname.includes('messenger')
          ) {
            toast(
              <ChatMessageToast
                messageData={msg}
                chats={chatStoreRef.getState().chats}
              />,
              {
                closeOnClick: false,
                onClick: () =>
                  navigate('/messenger', {
                    state: { id_user_in_chat: msg.sender_id },
                  }),
              }
            );
          }
          break;
        }

        case EVENT_TYPE.MESSAGE_READ: {
          const readData = event.message_read;
          if (readData) {
            chatStoreRef.getState().markAsRead(readData.chat_id, readData.message_id);
          }
          break;
        }

        case EVENT_TYPE.USER_STATUS_CHANGE: {
          const statusData = event.user_status_change;
          if (statusData) {
            chatStoreRef.getState().updateUserStatus(statusData.user_id, statusData.status ?? USER_STATUS.ONLINE);
          }
          break;
        }

        default:
          break;
      }
    }
  }, [lastMessage, lastJsonMessage, profile?.id, navigate, chatStoreRef]);
};
